import type { Fitting, SignedOn } from './assemblyStore';
import { digest } from './digest';
import { editsOf, madeOf, SURFACE_EDITS, type Job3D, type MeshEdit, type PiecePart } from './threeDApi';

// WHICH MODELS A SIGN-OFF WAS GIVEN ON, and whether they are still the ones on screen.
//
// A fitting is numbers — a turn, a seat, an axis — and numbers are about one mesh. Swap a part's
// model for one standing another way and the same numbers put it somewhere else: the spring
// washer's 1AA was answered against a screw lying along Y, the screw was rebuilt standing along
// Z, and the washer went on being turned 90° about X onto a screw that was no longer there,
// ticked as watched. Nothing said so, because nothing remembered what had been watched.
//
// So the tick keeps a mark per part, and the page compares. A mark that differs WITHDRAWS THE
// TICK (see SignOffSync): the fitting may well still be right, and only somebody watching it
// can tell — which is what the tick claims somebody did. It was said and not acted on, while a
// piece was a baked mesh that kept the old part inside it whatever happened to the part; a
// piece is now a list of its parts, and follows them.

// THE EDITS THAT LEAVE WHERE A PART STANDS ALONE (SURFACE_EDITS, see threeDApi) are not counted.
// Anything else changes the shape or the frame its numbers are written in (a turn, a rescale, a
// rebuild, a bore). Asked by the rules that stand a part where its set was built (fromSet,
// fromHole, jointMoves): one turned or rebuilt since is no longer in the set's frame.
//
// NOT WHAT A SIGN-OFF IS KEPT BY. It was: the mark was this count, and a count of edits is a
// guess at a mesh from a list about it — see partMark.
export const shapeEdits = (job: Job3D): number =>
  editsOf(job).filter((e) => !SURFACE_EDITS.includes(e.op)).length;

// WHICH MODEL A PART'S IS: its run, and THE SHAPE OF ITS MESH — a word the server reads off the
// file's own points (see glb.shape_print), the same for as long as the surface stands where it
// did and for no longer.
//
// IT WAS `<run>:<n>`, n being how many of its edits were of a kind taken to move the shape. Three
// things were wrong with that, and one of them cost a manual its thirty-four ticks: an edit of a
// kind nobody had classed counted as a move (materials applied to a bored frame: 2 became 3, and
// nothing had moved); a turn undone and made again another way counted the same as before; and a
// measurement nudged into the edit before it counted as nothing at all.
//
// A run the server reads no shape for — a piece baked to a file before pieces were lists, which
// nothing edits — is still marked the old way, by the old list, frozen: a word added to it would
// move every mark written.
const SURFACE_WAS: MeshEdit['op'][] = ['colour', 'grain', 'retouch', 'level', 'texture', 'pieces'];
const editsWas = (job: Job3D): number =>
  editsOf(job).filter((e) => !SURFACE_WAS.includes(e.op)).length;

const partMark = (job: Job3D): string =>
  (job.shape ? `${job.id}@${job.shape}` : `${job.id}:${editsWas(job)}`);

// WHETHER A MARK IS OF THE MESH THIS RUN IS NOW — the mark it would be given today, or one
// written the old way that was given on this same shape: the server says from which count the
// mesh has been the shape it is (`shapeSince`, walked back through the run's undo copies), and
// every count from there to now is a tick on what is standing.
const WAS = /^(\d+):(\d+)$/;
const isPart = (mark: string, job: Job3D): boolean => {
  if (mark === partMark(job)) return true;
  const was = job.shape ? WAS.exec(mark) : null;
  if (!was || Number(was[1]) !== job.id || job.shapeSince == null) return false;
  return Number(was[2]) >= job.shapeSince && Number(was[2]) <= editsWas(job);
};

// A PIECE IS MARKED BY WHAT IT IS MADE OF — which parts, which model of each, standing where —
// and not by its run. Signing a sub-step off again writes another run, and where nothing about
// the piece moved that must not read as a new model to every sub-step standing on it.
//
// To a thousandth of a millimetre: the same movement read off the same scene twice is the same
// numbers, and this is what keeps the last digit of a float from being a difference.

const madeMark = (made: PiecePart[], markOf: (p: PiecePart) => string | null): string =>
  `made:${digest(JSON.stringify(made.map((p) => [p.job, markOf(p), p.m.map((v) => Math.round(v * 1000))])))}`;

// Every model that stands for something now, by run.
export type Live = Map<number, Job3D>;

// EACH PART UNDER THE MARK ITS MODEL HAS TODAY, where it is still the model the piece was made
// with — so a piece listed with marks written the old way and the same piece signed off again
// this morning are one mark, and not a new model to everything standing on it. A part that has
// since become another keeps the mark it was listed under, which is then nobody's.
export const modelMark = (job: Job3D, live: Live): string => {
  const made = madeOf(job);
  if (!made) return partMark(job);
  return madeMark(made, (p) => {
    const now = live.get(p.job);
    return p.mark != null && now && isPart(p.mark, now) ? partMark(now) : p.mark;
  });
};

// The same question of any model, part or piece. A piece's mark written before the above was
// over its parts' marks as listed, and is still read.
export const isModel = (mark: string, job: Job3D, live: Live): boolean => {
  if (mark === modelMark(job, live)) return true;
  const made = madeOf(job);
  return made ? mark === madeMark(made, (p) => p.mark) : isPart(mark, job);
};

// WHETHER A PIECE IS STILL MADE OF THE MODELS IT WAS SIGNED OFF ON. A part listed under a run
// that no longer stands for anything, or whose mesh is another shape since, is another part.
export const stillMadeOf = (job: Job3D, live: Live): boolean =>
  (madeOf(job) ?? []).every((p) => {
    if (p.mark == null) return true;
    const now = live.get(p.job);
    return !!now && isPart(p.mark, now);
  });

// A part's model is found by its item; a piece's parts by their runs. The second off the first,
// once per map.
const lives = new WeakMap<Map<number, Job3D>, Live>();
export const liveOf = (modelOf: Map<number, Job3D>): Live => {
  let live = lives.get(modelOf);
  if (!live) {
    live = new Map([...modelOf.values()].map((job) => [job.id, job]));
    lives.set(modelOf, live);
  }
  return live;
};

type Drawn = Pick<Fitting, 'targetId' | 'movingId'>;
const ROLES = ['target', 'moving', 'tool'] as const;
const jobsOf = (fitting: Drawn, modelOf: Map<number, Job3D>, toolItemId: number | null | undefined) => {
  const of = (id: number | null | undefined) => (id != null ? modelOf.get(id) : undefined);
  return { target: of(fitting.targetId), moving: of(fitting.movingId), tool: of(toolItemId) };
};

// The marks of what the fitting draws now. Null for a part with no model: there is nothing to
// compare, and a part with no mesh is said elsewhere already.
export function marksNow(
  fitting: Drawn,
  modelOf: Map<number, Job3D>,
  toolItemId: number | null | undefined,
): SignedOn {
  const jobs = jobsOf(fitting, modelOf, toolItemId);
  const live = liveOf(modelOf);
  const of = (job: Job3D | undefined) => (job ? modelMark(job, live) : null);
  return { target: of(jobs.target), moving: of(jobs.moving), tool: of(jobs.tool) };
}

// WHICH OF THE PARTS A SET OF MARKS WAS WRITTEN ON HAVE ANOTHER MODEL NOW. Empty where no marks
// were kept — a sign-off given before they were: there is no telling what that one was watched
// on, and a warning on every old sign-off would say nothing about any of them.
//
// Of a sign-off's marks (`signedOn`), WHETHER OR NOT THE TICK STILL STANDS: it is withdrawn for
// exactly this, and the line saying which part moved on is then the only account of why.
export function changedSince(
  then: SignedOn | undefined,
  fitting: Drawn,
  modelOf: Map<number, Job3D>,
  toolItemId: number | null | undefined,
): (keyof SignedOn)[] {
  if (!then) return [];
  const jobs = jobsOf(fitting, modelOf, toolItemId);
  const live = liveOf(modelOf);
  return ROLES.filter((role) => {
    const was = then[role];
    const job = jobs[role];
    return was != null && !!job && !isModel(was, job, live);
  });
}

// THE SAME MARKS, EACH AS IT WOULD BE WRITTEN TODAY — or null where they already are. For the
// ones kept the old way (see partMark) that are still of the models drawn: rewritten once, they
// stop depending on the old reading being kept alive, and a piece signed off again under
// today's marks is still the piece they name.
export function marksRenewed(
  then: SignedOn | undefined,
  fitting: Drawn,
  modelOf: Map<number, Job3D>,
  toolItemId: number | null | undefined,
): SignedOn | null {
  if (!then || changedSince(then, fitting, modelOf, toolItemId).length) return null;
  const now = marksNow(fitting, modelOf, toolItemId);
  const next = { ...then };
  for (const role of ROLES) if (then[role] != null && now[role] != null) next[role] = now[role];
  return ROLES.some((role) => next[role] !== then[role]) ? next : null;
}

// WHETHER A SET OF MARKS WAS WRITTEN ON OTHER PARTS than the fitting draws now — a mark of a run
// that is the model of another item than the one in its role, or a mark where a part's role is empty.
// Not a model changed: a gesture changed, the marks of a sign-off given on another sub-step left
// at this one's position (see refile). Only what can be told: a piece's mark names no item, and a
// run that is no part's model today may be an old model of the same one.
const RUN = /^(\d+)[@:]/;
export function marksOfOtherParts(
  then: SignedOn | undefined,
  fitting: Drawn,
  modelOf: Map<number, Job3D>,
  toolItemId: number | null | undefined,
): boolean {
  if (!then) return false;
  const live = liveOf(modelOf);
  const items = { target: fitting.targetId, moving: fitting.movingId, tool: toolItemId ?? null };
  return ROLES.some((role) => {
    const mark = then[role];
    if (mark == null) return false;
    // An empty tool is not another gesture: the tool list may simply not have landed yet.
    if (items[role] == null) return role !== 'tool';
    const run = RUN.exec(mark);
    const job = run ? live.get(Number(run[1])) : undefined;
    return !!job && job.itemId != null && job.itemId !== items[role];
  });
}
